import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import Card from '../../ui/Card';
import Button from '../../ui/Button';
import Badge from '../../ui/Badge';
import Icon from '../../ui/Icon';
import { ICONS } from '../../ui/icon-map';
import { IMPORTERS } from '../../services/importers';
import { importLogService, IMPORT_LOG_ON } from '../../services/importLogService';
import { importUndoService } from '../../services/importUndoService';
import { finesService, errorMessage } from '../../services/finesService';
import { undoKindOf, storeUndoEntry } from '../../services/importRunner';
import { useModal } from '../../contexts/ModalContext';
import { usePermissions } from '../../hooks/usePermissions';
import logger from '../../utils/logger';
import { FreshnessBadge, isStale, fmtDate, ageText } from './components/ImportCard';
import { dubaiTime } from '../../utils/dubaiTime';

/**
 * The tables of `GET /ejar/status`, in the order the engine pulls them. The engine writes every one of them itself
 * each hour; nothing is uploaded here any more (owner, 28 Sep 2026: "no more manual lists - everything from Ejar").
 */
export const EJAR_TABLES = [
    { key: 'contacts', label: 'Customer contacts' },
    { key: 'airport_open', label: 'Airport open' },
    { key: 'airport_closed', label: 'Airport closed' },
    { key: 'business_bay', label: 'Business Bay' },
    { key: 'echeck', label: 'E-Check' },
    { key: 'fleet', label: 'Fleet (branch vehicles)' }
];

/** "07:05" (Dubai time) of an ISO timestamp, or '' when there is none. */
export const fmtClock = (iso) => {
    return dubaiTime(iso);
};

/**
 * One hub card per importer (the staff list): title, table, last update, freshness badge, description.
 * Opens /database/<id>; "Undo last import" opens the same page with the undo
 * confirmation of the card already open (the undo itself runs there).
 */
function HubCard({ importer, lastLog, loaded, canUndo }) {
    const stale = isStale(lastLog);
    const navigate = useNavigate();
    const openUndo = (e) => {
        e.preventDefault();
        e.stopPropagation();
        navigate(`/database/${importer.id}?undo=1`);
    };
    return (
        <Link to={`/database/${importer.id}`} className="block h-full no-underline text-inherit">
            <Card interactive className="h-full">
                <div className="flex items-start gap-3">
                    <Icon glyph={ICONS.upload} size="lg" tone="primary" />
                    <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="text-base font-semibold text-gray-900 m-0">{importer.title}</h3>
                            {IMPORT_LOG_ON && loaded && <FreshnessBadge lastLog={lastLog} />}
                        </div>
                        <p className="text-xs text-gray-500 m-0">Table: <code>{importer.table}</code></p>
                        {IMPORT_LOG_ON && <p className={`text-xs m-0 mt-1 font-medium ${!loaded ? 'text-gray-500' : stale ? 'text-red-600' : 'text-green-700'}`}>
                            Last update: {!loaded ? 'loading...' : lastLog ? `${fmtDate(lastLog.at)}${lastLog.user ? ` by ${lastLog.user}` : ''} (${ageText(lastLog.at)})` : 'never'}
                        </p>}
                        <p className="text-xs text-gray-600 m-0 mt-2 line-clamp-3">{importer.description}</p>
                        {canUndo && (
                            <Button size="xs" variant="danger" className="mt-2" onClick={openUndo}>
                                Undo last import
                            </Button>
                        )}
                    </div>
                </div>
            </Card>
        </Link>
    );
}

/** One chip per table of `GET /ejar/status`: rows and the time the engine pulled it. */
function TableChip({ label, entry }) {
    if (!entry || (!entry.file && !entry.error)) {
        return <Badge variant="neutral"><span className="font-semibold">{label}</span> &middot; not pulled yet</Badge>;
    }
    if (entry.error) {
        return <Badge variant="danger" title={String(entry.error)}><span className="font-semibold">{label}</span> &middot; failed</Badge>;
    }
    return (
        <Badge variant="neutral" title={entry.file}>
            <span className="font-semibold">{label}</span>
            {typeof entry.rows === 'number' ? ` · ${entry.rows.toLocaleString()} rows` : ''}
            {entry.at ? ` · ${fmtClock(entry.at)}` : ''}
        </Badge>
    );
}

/** The engine's hourly Ejar sync: when it last ran, what it pulled, and a manual "Sync now". */
export function EjarPanel({ status, statusError, loadingStatus, onSync, syncing, canSync = true }) {
    return (
        <Card className="mb-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <Icon glyph={ICONS.cloud_sync} size="lg" tone="primary" />
                        <h3 className="text-base font-semibold text-gray-900 m-0">Ejar hourly sync</h3>
                        {status && status.enabled === false && <Badge variant="neutral" size="sm">Disabled</Badge>}
                        {status && status.running && <Badge variant="info" size="sm" dot>Syncing...</Badge>}
                    </div>
                    <p className="text-xs text-gray-600 m-0 mt-1">The engine pulls these from Ejar every hour and writes them itself.</p>
                    <p className="text-xs text-gray-600 m-0 mt-1">
                        {loadingStatus ? 'Loading the engine status...' : statusError
                            ? <span className="text-red-600">Engine unavailable: {statusError}</span>
                            : <>Last sync: <strong>{status?.last_run ? fmtDate(status.last_run) : 'never'}</strong> &middot; Next sync: <strong>{status?.next_run ? fmtDate(status.next_run) : 'not scheduled'}</strong></>}
                    </p>
                    {status?.last_error && <p className="text-xs text-red-600 m-0 mt-1">Last error: {String(status.last_error)}</p>}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    {canSync && <Button variant="secondary" size="sm" leadingIcon={<Icon glyph={ICONS.refresh} size="sm" />} onClick={onSync} isLoading={syncing} disabled={syncing}>
                        Sync now
                    </Button>}
                </div>
            </div>

            {status?.tables && (
                <div className="flex flex-wrap gap-2 mt-3">
                    {EJAR_TABLES.map(t => <TableChip key={t.key} label={t.label} entry={status.tables[t.key]} />)}
                </div>
            )}
        </Card>
    );
}

/**
 * Database page: the engine's hourly Ejar sync (status + "Sync now") and the staff list import - the one list
 * still imported by hand. Contracts, airport contracts, contacts, the E-Check archive and the fleet come from
 * Ejar through the engine (owner, 28 Sep 2026: "no more manual lists - everything from Ejar").
 */
export default function Database() {
    const { showToast } = useModal();
    const [logs, setLogs] = useState({}); // table -> import_log entry | null
    const [undoable, setUndoable] = useState({}); // table -> true when the last import can still be undone
    // undoing an import is the import's own permission, like the import itself (owner, 3 Oct 2026)
    const { hasPermission, hasAllPermissions, hasAnyPermission } = usePermissions();
    const mayImport = (imp) => (imp.permissions
        ? (imp.requireAll !== false ? hasAllPermissions(imp.permissions) : hasAnyPermission(imp.permissions))
        : hasPermission(imp.permission));
    const [loaded, setLoaded] = useState(false);

    const [status, setStatus] = useState(null);
    const [statusError, setStatusError] = useState(null);
    const [loadingStatus, setLoadingStatus] = useState(true);
    const [syncing, setSyncing] = useState(false);

    const loadLogs = useCallback(async () => {
        const tables = Array.from(new Set(IMPORTERS.map(i => i.table)));
        const entries = await Promise.all(tables.map(async t => [t, await importLogService.getLatest(t)]));
        const byTable = Object.fromEntries(entries);
        setLogs(byTable);
        setLoaded(true);

        // One extra read per table that supports undo and whose log names its entry; the store's own undo (the staff
        // import under USE_CONTRACTS_V2) is this session's last import, no read
        const undoTables = IMPORTERS.filter(i => undoKindOf(i) === 'snapshot' && byTable[i.table]?.entry_id);
        const snapshots = await Promise.all(undoTables.map(async i => [i.table, !!(await importUndoService.getReadyEntryFor(i.table, byTable[i.table]))]));
        const storeUndos = IMPORTERS.filter(i => undoKindOf(i) === 'store').map(i => [i.table, !!storeUndoEntry(i)]);
        setUndoable(Object.fromEntries([...snapshots, ...storeUndos]));
        return byTable;
    }, []);

    const loadStatus = useCallback(async () => {
        setLoadingStatus(true);
        try {
            const s = await finesService.ejarStatus();
            setStatus(s);
            setStatusError(null);
            return s;
        } catch (err) {
            logger.warn('[Database] Ejar status unavailable:', err?.message || err);
            setStatus(null);
            setStatusError(errorMessage(err));
            return null;
        } finally {
            setLoadingStatus(false);
        }
    }, []);

    useEffect(() => { loadLogs(); loadStatus(); }, [loadLogs, loadStatus]);

    // Keep the engine status fresh while the page stays open (every 5 min), so "Syncing..." clears and the
    // chips move on without a reload.
    useEffect(() => {
        const id = setInterval(() => { loadStatus(); }, 5 * 60 * 1000);
        return () => clearInterval(id);
    }, [loadStatus]);

    const syncNow = async () => {
        setSyncing(true);
        try {
            await finesService.ejarSyncNow();
            if (showToast) showToast('Ejar sync started; the tables update when it finishes.', 'success');
            await loadStatus();
        } catch (err) {
            logger.error('[Database] Ejar sync failed:', err);
            if (showToast) showToast(`Ejar sync could not start: ${errorMessage(err)}`, 'error');
        } finally {
            setSyncing(false);
        }
    };

    return (
        <Layout title="Database" subtitle="The hourly Ejar sync and the staff list import" backPath="/">
            <div className="max-w-[1500px] mx-auto px-2">
                <EjarPanel
                    status={status} statusError={statusError} loadingStatus={loadingStatus}
                    onSync={syncNow} syncing={syncing} canSync={hasPermission('run_ejar_sync')}
                />

                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 items-stretch">
                    {IMPORTERS.map(importer => (
                        <HubCard key={importer.id} importer={importer} lastLog={logs[importer.table] || null} loaded={loaded}
                            canUndo={!!undoable[importer.table] && mayImport(importer)} />
                    ))}
                </div>
            </div>
        </Layout>
    );
}
